import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { getValidationSchema } from '../../util/helpers/getValidationSchema';

import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import useStepPost from '../../hooks/useStepPost';
import useTrustNameDispatch from '../../hooks/useTrustNameDispatch';
import { FormWrap } from '../FormContainer/FormContainer';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import InputText from '../Fields/InputText';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const formValidationSchema = Yup.object({
  ...getValidationSchema({
    fields: ['TrustName'],
  }),
});

const formValidationSchemaImpersonate = Yup.object({
  ...getValidationSchemaImpersonate({
    fields: ['TrustName'],
  }),
});

const TrustNameForm = ({
  applicantType,
  nextPath,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const ApplicantData = useApplicantData(applicantType);
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { trustNameDispatch } = useTrustNameDispatch();
  const { t } = useTranslation();
  const defaultValues = {
    TrustName: getApplicantData(ApplicantData, 'Name'),
  };

  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Secure Trust Name',
      accountType: accountGtmType,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Trust Name',
      accountType: accountGtmType,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
    defaultValues,
  });

  const onSubmit = ({ TrustName }) => {
    trustNameDispatch({
      data: {
        TrustName,
      },
      ApplicantData,
      onSuccess:
        eventType === 'Form'
          ? onSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError: () => {
        setHasError(true);
        setSaveAndCloseModalOpen(true);
      },
      defaultValues,
    });
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            label={t('trustName')}
            {...field}
            {...fieldState}
            autoFocus
          />
        )}
        name="TrustName"
      />
      <OnBoardingCta
        type="submit"
        onClick={() => {
          setEventType('Form');
          handleGtmEvent();
        }}
        title={t('common.continue')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

TrustNameForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default TrustNameForm;
